import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { AuthService } from '../services/auth.service';
import { BillingService, SubscriptionStatus } from '../services/billing.service';
import { PaymentMethodDto, PaymentService } from '../services/payment.service';
import { STRIPE_APPEARANCE } from '../services/stripe-appearance';
import { PLANS, PlanData } from '../../plan-detail/plan-detail.component';

type CheckoutState = 'loading' | 'ready' | 'not-provider' | 'already-subscribed' | 'unavailable' | 'success';

const TRIAL_DAYS = 14;

// Self-serve subscription checkout (Pro). The card is collected with Stripe's Payment
// Element against a SetupIntent — card data goes straight to Stripe — and the resulting
// payment method is handed to BillingController, which starts the subscription/trial.
@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.css']
})
export class CheckoutComponent implements OnInit, OnDestroy {
  @ViewChild('paymentElementRef') paymentElementRef?: ElementRef<HTMLDivElement>;

  plan: PlanData | null = null;
  state: CheckoutState = 'loading';
  user: any = null;
  subscription: SubscriptionStatus | null = null;

  savedCards: PaymentMethodDto[] = [];
  selectedCardId: string | 'new' = 'new';

  elementReady = false;
  elementComplete = false;
  submitting = false;
  errorMessage = '';

  trialEndDate: Date | null = null;
  nextChargeDate: Date | null = null;

  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private billingService: BillingService,
    private paymentService: PaymentService
  ) {}

  get trialEligible(): boolean {
    return this.subscription?.trialEligible !== false;
  }

  get priceAmount(): number {
    return Number((this.plan?.price || '0').replace(/[^0-9.]/g, '')) || 0;
  }

  get trialEndsOn(): Date {
    const d = new Date();
    d.setDate(d.getDate() + TRIAL_DAYS);
    return d;
  }

  get trialDays(): number {
    return TRIAL_DAYS;
  }

  get featureHighlights(): string[] {
    if (!this.plan) return [];
    return this.plan.featureGroups.map(g => g.items[0]).concat(this.plan.featureGroups[1]?.items.slice(1, 3) || []).slice(0, 5);
  }

  get submitLabel(): string {
    if (this.submitting) return 'Processing…';
    return this.trialEligible ? `Start ${TRIAL_DAYS}-day free trial` : `Subscribe · $${this.priceAmount}/month`;
  }

  get canSubmit(): boolean {
    if (this.submitting) return false;
    if (this.selectedCardId === 'new') return this.elementReady && this.elementComplete;
    return true;
  }

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id') || '';
    this.plan = PLANS[id] || null;
    if (!this.plan || this.plan.id !== 'pro') {
      // Only Pro is self-serve; Free and Enterprise have their own CTAs on the plan page.
      this.router.navigate(['/plans', id || 'pro']);
      return;
    }

    this.user = this.authService.getCurrentUser();
    if (!this.user) {
      this.router.navigate(['/auth'], { queryParams: { returnUrl: this.router.url, role: 'provider', mode: 'signup' } });
      return;
    }

    if (this.user.role !== 'provider' && this.user.role !== 'admin') {
      this.state = 'not-provider';
      return;
    }

    try {
      const [subscription, cards, stripe] = await Promise.all([
        firstValueFrom(this.billingService.getSubscription(this.user.id)),
        firstValueFrom(this.paymentService.getPaymentMethods(this.user.id)).catch(() => [] as PaymentMethodDto[]),
        this.paymentService.getStripe(),
      ]);

      this.subscription = subscription;
      if (subscription.tier === this.plan.id && (subscription.status === 'active' || subscription.status === 'trialing')) {
        this.state = 'already-subscribed';
        return;
      }

      if (!stripe) {
        this.state = 'unavailable';
        return;
      }
      this.stripe = stripe;

      this.savedCards = cards;
      const defaultCard = cards.find(c => c.isDefault) || cards[0];
      this.selectedCardId = defaultCard ? defaultCard.id : 'new';

      this.state = 'ready';
      if (this.selectedCardId === 'new') {
        setTimeout(() => this.mountPaymentElement());
      }
    } catch {
      this.state = 'unavailable';
    }
  }

  ngOnDestroy(): void {
    this.paymentElement?.destroy();
  }

  selectCard(id: string | 'new'): void {
    this.selectedCardId = id;
    this.errorMessage = '';
    if (id === 'new' && !this.paymentElement) {
      setTimeout(() => this.mountPaymentElement());
    }
  }

  private mountPaymentElement(): void {
    if (!this.stripe || !this.paymentElementRef || this.paymentElement) return;

    // Deferred mode: no SetupIntent is created until the user actually submits.
    this.elements = this.stripe.elements({
      mode: 'setup',
      currency: 'usd',
      paymentMethodTypes: ['card'],
      appearance: STRIPE_APPEARANCE,
    });

    this.paymentElement = this.elements.create('payment', {
      layout: 'tabs',
      defaultValues: {
        billingDetails: {
          name: [this.user?.firstName, this.user?.lastName].filter(Boolean).join(' '),
          email: this.user?.email || '',
        },
      },
      terms: { card: 'never' },
    });
    this.paymentElement.on('ready', () => this.elementReady = true);
    this.paymentElement.on('change', e => {
      this.elementComplete = e.complete;
      if (this.errorMessage && e.complete) this.errorMessage = '';
    });
    this.paymentElement.mount(this.paymentElementRef.nativeElement);
  }

  async submit(): Promise<void> {
    if (!this.canSubmit || !this.stripe || !this.plan) return;
    this.submitting = true;
    this.errorMessage = '';

    try {
      let paymentMethodId: string | undefined;

      if (this.selectedCardId === 'new') {
        const { error: submitError } = await this.elements!.submit();
        if (submitError) {
          throw new Error(submitError.message);
        }

        const { clientSecret } = await firstValueFrom(this.paymentService.createSetupIntent(this.user.id));
        const { error, setupIntent } = await this.stripe.confirmSetup({
          elements: this.elements!,
          clientSecret,
          redirect: 'if_required',
          confirmParams: { return_url: window.location.href },
        });
        if (error) {
          throw new Error(error.message);
        }
        paymentMethodId = typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;
      } else {
        paymentMethodId = this.selectedCardId;
      }

      const result = await firstValueFrom(this.billingService.subscribe(this.user.id, this.plan.id, paymentMethodId));

      if (result.requiresAction && result.clientSecret) {
        const confirmation = result.actionType === 'setup'
          ? await this.stripe.confirmCardSetup(result.clientSecret)
          : await this.stripe.confirmCardPayment(result.clientSecret);
        if (confirmation.error) {
          throw new Error(confirmation.error.message || 'Your bank declined the verification. Please try another card.');
        }
      }

      this.trialEndDate = result.trialEnd ? new Date(result.trialEnd) : null;
      this.nextChargeDate = result.trialEnd
        ? new Date(result.trialEnd)
        : result.currentPeriodEnd ? new Date(result.currentPeriodEnd) : null;
      this.state = 'success';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Something went wrong. Your card was not charged — please try again.';
    } finally {
      this.submitting = false;
    }
  }

  retry(): void {
    this.state = 'loading';
    this.ngOnInit();
  }

  switchAccount(): void {
    this.authService.logout();
    this.router.navigate(['/auth'], { queryParams: { returnUrl: this.router.url, role: 'provider', mode: 'signup' } });
  }

  brandLabel(brand: string): string {
    const map: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover' };
    return map[brand?.toLowerCase()] || (brand ? brand[0].toUpperCase() + brand.slice(1) : 'Card');
  }

  backToPlan(): void {
    this.router.navigate(['/plans', this.plan?.id || 'pro']);
  }

  goToDashboard(): void {
    this.router.navigate(['/provider-dashboard']);
  }

  goToBilling(): void {
    this.router.navigate(['/payments']);
  }

  becomeProvider(): void {
    this.router.navigate(['/become-provider']);
  }
}
